import React, { useEffect, useState } from "react";
import { Loader2, DollarSign, Receipt, Target, TrendingUp, PackageSearch } from "lucide-react";
import { sr } from "../../lib/sr";

const money = (v, c = "USD") => `${c === "USD" ? "$" : ""}${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function SalesReport() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);

  const load = (d) => { setData(null); sr.salesReport(d).then(setData).catch(() => setData({ has_orders: false, totals: {}, campaigns: [], top_products: [] })); };
  useEffect(() => { load(days); }, [days]);

  if (!data) return <div className="sr-empty" style={{ padding: 60, textAlign: "center" }}><Loader2 className="sr-spin" /></div>;
  const t = data.totals || {};
  const cur = t.currency || "USD";
  const rate = Math.round((t.attribution_rate || 0) * 100);

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "8px 4px 40px" }} data-testid="sales-report-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <h1 style={{ color: "var(--navy)", fontSize: 26, margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Receipt size={22} /> Verified Sales
        </h1>
        <select className="sr-input sr-input-sm" style={{ width: "auto" }} value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="sales-range">
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={0}>All time</option>
        </select>
      </div>
      <p className="sr-muted" style={{ marginTop: 6 }}>Real revenue from your synced Shopify orders, tied back to the campaigns that promote each product.</p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 12, marginTop: 14 }} data-testid="sales-kpis">
        {[
          ["Total revenue", money(t.revenue, cur), DollarSign],
          ["Orders", (t.orders ?? 0).toLocaleString(), Receipt],
          ["Attributed to campaigns", money(t.attributed_revenue, cur), Target],
          ["Attribution rate", `${rate}%`, TrendingUp],
        ].map(([label, val, Icon]) => (
          <div key={label} className="sr-card" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Icon size={16} style={{ color: "var(--blue)" }} />
            <div className="sr-muted" style={{ fontSize: 12 }}>{label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: "var(--navy)" }}>{val}</div>
          </div>
        ))}
      </div>

      {!data.has_orders ? (
        <div className="sr-card sr-empty" style={{ marginTop: 14, textAlign: "center", padding: 34 }} data-testid="sales-empty">
          <PackageSearch size={26} style={{ color: "var(--muted)" }} />
          <p style={{ marginTop: 8 }}>No synced orders yet. Connect your Shopify store and run a sync, then link products to your campaigns to see revenue attributed here.</p>
        </div>
      ) : (
        <>
          <h3 style={{ color: "var(--navy)", marginTop: 22, marginBottom: 8 }}>Revenue by campaign</h3>
          {data.campaigns.length === 0 ? (
            <div className="sr-card sr-muted" style={{ padding: 16, fontSize: 13 }} data-testid="sales-no-attribution">
              Orders are syncing, but none map to a campaign yet. Link the products each campaign promotes (Inventory Guard → Linked products) to attribute revenue.
            </div>
          ) : (
            <div className="sr-card" style={{ padding: 0, overflow: "hidden" }} data-testid="sales-campaign-table">
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
                  <th style={{ padding: "10px 14px" }}>Campaign</th><th style={{ padding: "10px 14px" }}>Status</th>
                  <th style={{ padding: "10px 14px", textAlign: "right" }}>Revenue</th>
                  <th style={{ padding: "10px 14px", textAlign: "right" }}>Orders</th>
                  <th style={{ padding: "10px 14px", textAlign: "right" }}>Products</th>
                </tr></thead>
                <tbody>
                  {data.campaigns.map((c) => (
                    <tr key={c.campaign_id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`sales-campaign-${c.campaign_id}`}>
                      <td style={{ padding: "10px 14px", fontWeight: 600, color: "var(--navy)" }}>{c.name}</td>
                      <td style={{ padding: "10px 14px" }}><span className={`sr-badge ${c.status === "ACTIVE" || c.status === "PUBLISHED" ? "sr-badge-green" : "sr-badge-gray"}`}>{c.status || "—"}</span></td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700 }}>{money(c.revenue, cur)}</td>
                      <td style={{ padding: "10px 14px", textAlign: "right" }}>{c.orders}</td>
                      <td style={{ padding: "10px 14px", textAlign: "right" }}>{c.products}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.top_products.length > 0 && (
            <>
              <h3 style={{ color: "var(--navy)", marginTop: 22, marginBottom: 8 }}>Top products</h3>
              <div style={{ display: "grid", gap: 8 }} data-testid="sales-top-products">
                {data.top_products.map((p) => (
                  <div key={p.item_id} className="sr-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px" }}>
                    <span style={{ color: "var(--navy)", fontWeight: 600 }}>{p.name || "Product"}</span>
                    <span className="sr-muted" style={{ fontSize: 13 }}>{p.qty} sold · <strong style={{ color: "var(--navy)" }}>{money(p.revenue, cur)}</strong></span>
                  </div>
                ))}
              </div>
            </>
          )}

          <h3 style={{ color: "var(--navy)", marginTop: 22, marginBottom: 4 }}>Revenue by source (UTM)</h3>
          <p className="sr-muted" style={{ marginTop: 0, fontSize: 12.5 }}>
            Attribution from the <code>utm_source / utm_medium / utm_campaign</code> tags your synced orders carry.
            {data.by_utm?.proposal_utm_campaign ? <> Won-proposal campaign: <code>{data.by_utm.proposal_utm_campaign}</code>.</> : null}
          </p>
          {!data.by_utm?.has_utm ? (
            <div className="sr-card sr-muted" style={{ padding: 16, fontSize: 13 }} data-testid="utm-empty">
              No UTM data on your synced orders yet. Tag your ad links with <code>utm_source</code>, <code>utm_medium</code> and <code>utm_campaign</code> so each sale ties back to the channel that drove it.
            </div>
          ) : (
            <div className="sr-card" style={{ padding: 0, overflow: "hidden" }} data-testid="utm-table">
              <div style={{ padding: "8px 14px", fontSize: 12, color: "var(--muted)", background: "#F8FAFC" }}>
                {Math.round((data.by_utm.utm_coverage || 0) * 100)}% of orders carry UTM tags ({data.by_utm.orders_with_utm} of {t.orders})
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
                  <th style={{ padding: "10px 14px" }}>Source</th><th style={{ padding: "10px 14px" }}>Medium</th>
                  <th style={{ padding: "10px 14px" }}>Campaign</th>
                  <th style={{ padding: "10px 14px", textAlign: "right" }}>Revenue</th>
                  <th style={{ padding: "10px 14px", textAlign: "right" }}>Orders</th>
                </tr></thead>
                <tbody>
                  {data.by_utm.rows.map((r, i) => (
                    <tr key={i} style={{ borderTop: "1px solid var(--border)" }} data-testid={`utm-row-${i}`}>
                      <td style={{ padding: "10px 14px", fontWeight: 600, color: "var(--navy)" }}>{r.source}</td>
                      <td style={{ padding: "10px 14px" }}>{r.medium}</td>
                      <td style={{ padding: "10px 14px" }}>
                        {r.campaign}{r.from_won_proposal ? <span className="sr-badge sr-badge-green" style={{ marginLeft: 6 }}>Won proposal</span> : null}
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700 }}>{money(r.revenue, cur)}</td>
                      <td style={{ padding: "10px 14px", textAlign: "right" }}>{r.orders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
